import { type FC, useContext, useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { useAuth, useNotifications, useQueryRequest } from '../../../modules/hooks';
import type { LockRecord, ProviderLock, RoomResponse } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { useLocks } from '../../Dashboards/LockManagement/useLocks';

export type LockModalMode = 'create' | 'edit' | 'view';

interface LockModalProps {
  show: boolean;
  mode: LockModalMode;
  data?: LockRecord;
  handleClose: () => void;
  onSaved?: () => void;
}

export const LockModal: FC<LockModalProps> = ({
  show,
  mode,
  data,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const { credentialsInfo } = useAuth()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const propertyId = selectedProperty?.property.uuid ?? '';
  const locksService = useLocks();

  // Single provider per property for now: the lock inherits the property's
  // configured door-access provider (no per-lock selection, to avoid mistakes).
  const provider = selectedProperty?.property?.doorAccessProvider;

  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [roomUuid, setRoomUuid] = useState('');
  const [providerLocks, setProviderLocks] = useState<ProviderLock[]>([]);
  const [saving, setSaving] = useState(false);

  const readOnly = mode === 'view';

  const { data: roomsData } = useQueryRequest<RoomResponse[]>(
    endpoints.ROOMS,
    {
      authToken: credentialsInfo?.token,
      propertyId,
    },
    { manual: !credentialsInfo?.token || !propertyId },
  );
  const rooms = roomsData?.result ?? [];

  useEffect(() => {
    if (!show) return;
    setName(data?.name ?? '');
    setExternalId(data?.externalId ?? '');
    setExternalReference(data?.externalReference ?? '');
    setRoomUuid(data?.room?.uuid ?? '');
    // Loaded in every mode so we can show the device's name (not its id),
    // including in read-only view.
    locksService
      .listProviderLocks()
      .then(setProviderLocks)
      .catch(() =>
        setSimpleToasts({
          type: 'danger',
          message: 'Failed to load provider devices',
          show: true,
        }),
      );
  }, [show, data, locksService, setSimpleToasts]);

  // The selected device's display name (falls back to the lock name, then the id).
  const deviceName =
    providerLocks.find((lock) => lock.id === externalId)?.name || name || externalId;

  const onSelectDevice = (deviceId: string) => {
    setExternalId(deviceId);
    const device = providerLocks.find((lock) => lock.id === deviceId);
    if (device) {
      if (!name.trim()) setName(device.name);
      if (device.reference) setExternalReference(device.reference);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setSimpleToasts({
        type: 'danger',
        message: 'Please enter a lock name',
        show: true,
      });
      return;
    }
    if (!externalId.trim()) {
      setSimpleToasts({ type: 'danger', message: 'Please select a device', show: true });
      return;
    }
    setSaving(true);
    try {
      if (mode === 'edit' && data) {
        await locksService.update(data.uuid, {
          name: name.trim(),
          externalId: externalId.trim(),
          externalReference: externalReference.trim() || undefined,
          provider: provider ?? null,
          roomUuid: roomUuid || null,
        });
      } else {
        await locksService.create({
          name: name.trim(),
          externalId: externalId.trim(),
          externalReference: externalReference.trim() || undefined,
          provider,
          roomUuid: roomUuid || undefined,
        });
      }
      setSimpleToasts({ type: 'success', message: 'Lock saved', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({ type: 'danger', message: 'Failed to save lock', show: true });
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create' ? 'New lock' : mode === 'edit' ? 'Edit lock' : 'View lock';

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Lock name</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Front Door"
            value={name}
            disabled={readOnly}
            onChange={(event) => setName(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Provider</Form.Label>
          {/* Read-only for now: inherited from the selected property's
              door-access provider (per-property providers come later). */}
          <Form.Select value={provider ?? ''} disabled>
            {provider ? (
              <option value={provider}>{provider}</option>
            ) : (
              <option value="">No provider configured</option>
            )}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Provider device</Form.Label>
          {readOnly ? (
            <Form.Control type="text" value={deviceName} disabled />
          ) : (
            <Form.Select
              value={externalId}
              onChange={(event) => onSelectDevice(event.target.value)}
            >
              <option value="">Select a device…</option>
              {/* Keep the current device selectable even if it's no longer
                  returned by the provider list (e.g. while editing). */}
              {externalId && !providerLocks.some((lock) => lock.id === externalId) && (
                <option value={externalId}>{name || externalId}</option>
              )}
              {providerLocks.map((lock) => (
                <option key={lock.id} value={lock.id}>
                  {lock.name}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>External reference</Form.Label>
          <Form.Control
            type="text"
            value={externalReference}
            disabled={readOnly}
            onChange={(event) => setExternalReference(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Room (direct attachment)</Form.Label>
          <Form.Select
            value={roomUuid}
            disabled={readOnly}
            onChange={(event) => setRoomUuid(event.target.value)}
          >
            <option value="">No direct room</option>
            {rooms.map((room) => (
              <option key={room.uuid} value={room.uuid}>
                {room.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
